import type { FC } from "react";

import { cn } from "@/lib/utils";
import { m } from "@recobra/i18n/messages";
import { Skeleton } from "@recobra/ui/skeleton";

import { UserAvatarLabel } from "./UserAvatarLabel";
import { useUserProfile } from "./useUserProfile";

/**
 * The one way to render who someone is: always avatar + name. Resolves the
 * profile behind a user id and shows a skeleton until it lands, so a raw id
 * never reaches the screen; `name` is shown when the profile has none. Email is
 * the last resort, below any name the caller supplied.
 */
export const UserLabel: FC<{
  userId: string | null | undefined;
  /** Name the caller already holds, used when the profile has no name. */
  name?: string;
  /** Rendered when there is no user at all, e.g. an unassigned task. */
  emptyLabel?: string;
  /** `xs` for dense one-line contexts. */
  size?: "sm" | "xs";
  className?: string;
  isBusy?: boolean;
}> = ({ userId, name, emptyLabel, size = "sm", className, isBusy }) => {
  const { profile, isLoading } = useUserProfile(userId);
  const resolved = profile?.name?.trim() || name?.trim() || profile?.email?.trim();

  if (!resolved && isLoading) {
    return <UserLabelSkeleton className={className} />;
  }

  if (!resolved && !userId) {
    return (
      <span
        className={cn(
          "inline-block max-w-full truncate align-bottom text-muted-foreground",
          className,
        )}
      >
        {emptyLabel ?? m.common_em_dash()}
      </span>
    );
  }

  return (
    <UserAvatarLabel
      name={resolved ?? m.user_unknown()}
      picture={profile?.picture}
      size={size}
      className={className}
      isBusy={isBusy}
    />
  );
};

const UserLabelSkeleton: FC<{ className?: string }> = ({ className }) => (
  <div className={cn("flex min-w-0 items-center gap-2", className)}>
    <Skeleton className="size-6 shrink-0 rounded-full" />
    <Skeleton className="h-4 w-24" />
  </div>
);
